import React, { useCallback } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import type { DineKitchen } from 'api/types';

/**
 * Kitchen Details (SETUP-009): read-only, per the template's view modal (label above a bordered
 * value, two columns, status badge). Its stations are listed with printer and fallback. Delete
 * starts here, from the reviewed record; a kitchen with stations is refused by the server.
 */

interface Props {
  isOpen: boolean;
  record: DineKitchen | null;
  canEdit: boolean;
  canDelete: boolean;
  tabId?: string;
  moduleName: string;
  onClose: () => void;
  onEdit: (record: DineKitchen) => void;
  onDelete: (record: DineKitchen) => void;
  onRestoreView: (recordId: string) => void;
}

const ViewField: React.FC<{ label: string; value?: React.ReactNode; fullWidth?: boolean }> = ({ label, value, fullWidth }) => (
  <div style={fullWidth ? { gridColumn: '1 / -1' } : {}}>
    <label className="form-label">{label}</label>
    <div style={{
      padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem',
      border: '1px solid var(--form-border-color)', fontSize: '0.875rem', minHeight: '36px',
    }}>
      {value === undefined || value === null || value === '' ? <span style={{ color: 'var(--text-muted)' }}>—</span> : value}
    </div>
  </div>
);

const when = (at?: string, by?: string) => (at ? `${new Date(at).toLocaleString()}${by ? ` · ${by}` : ''}` : undefined);

const DineOpsKitchenViewModal: React.FC<Props> = ({
  isOpen, record, canEdit, canDelete, tabId, moduleName, onClose, onEdit, onDelete, onRestoreView,
}) => {
  const { minimize } = useModalMinimize();

  const handleMinimize = useCallback(() => {
    if (!tabId || !record) return;
    const id = record.id;
    minimize({
      id: `${tabId}-view-modal-${id}`,
      title: `View - ${record.kitchenCode}`,
      moduleName,
      tabId,
      onRestore: () => onRestoreView(id),
    });
    onClose();
  }, [tabId, record, minimize, moduleName, onClose, onRestoreView]);

  if (!isOpen || !record) return null;
  const stations = record.stations ?? [];

  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div className="modal-container modal-lg">
        <div className="modal-header">
          <h2 className="modal-title">Kitchen Details</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar" type="button">
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={onClose} type="button"><IconSystem.x size={20} /></button>
          </div>
        </div>

        <div className="modal-content">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <ViewField label="Code" value={<span style={{ fontFamily: 'monospace' }}>{record.kitchenCode}</span>} />
            <ViewField label="Kitchen name" value={record.kitchenName} />
            <ViewField label="Property" value={record.propertyName ?? 'Shared by every property'} />
            <ViewField label="Business unit" value={record.businessUnitName ?? <span style={{ color: 'var(--text-muted)' }}>Not linked yet</span>} />
            <ViewField label="Display order" value={record.displayOrder} />
            <ViewField label="Status" value={<span className={`badge badge-${record.isActive ? 'active' : 'inactive'}`}>{record.isActive ? 'Active' : 'Inactive'}</span>} />
            <ViewField label="Created" value={when(record.createdAt, record.createdByName)} />
            <ViewField label="Updated" value={when(record.updatedAt, record.updatedByName)} />
          </div>

          <div style={{ marginTop: '1.25rem' }}>
            <label className="form-label">Kitchen stations ({stations.length})</label>
            <div style={{ border: '1px solid var(--border-color)', borderRadius: '0.375rem', overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
                    {['Code', 'Station', 'Printer', 'Fallback', 'Used by', 'Now'].map((h) => (
                      <th key={h} style={{ padding: '0.4rem 0.5rem', borderBottom: '1px solid var(--border-color)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {stations.length === 0 && (
                    <tr><td colSpan={6} style={{ padding: '0.5rem', color: 'var(--text-muted)' }}>No stations in this kitchen.</td></tr>
                  )}
                  {stations.map((s) => (
                    <tr key={s.id ?? s.stationCode} style={{ opacity: s.isActive ? 1 : 0.6 }}>
                      <td style={{ padding: '0.4rem 0.5rem', fontFamily: 'monospace' }}>{s.stationCode}</td>
                      <td style={{ padding: '0.4rem 0.5rem' }}>{s.stationName}{s.isActive ? '' : ' (inactive)'}</td>
                      <td style={{ padding: '0.4rem 0.5rem' }}>{s.primaryPrinterName ?? <span style={{ color: 'var(--text-muted)' }}>Screen only</span>}</td>
                      <td style={{ padding: '0.4rem 0.5rem' }}>{s.fallbackStationName ?? '—'}</td>
                      <td style={{ padding: '0.4rem 0.5rem' }}>{s.usedBy ?? <span style={{ color: 'var(--text-muted)' }}>no outlet</span>}</td>
                      <td style={{ padding: '0.4rem 0.5rem' }}>
                        <span className={`badge badge-${s.isOpen === false ? 'inactive' : 'active'}`}>{s.isOpen === false ? 'Closed' : 'Open'}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          {canDelete && (
            <button className="btn btn-danger" onClick={() => onDelete(record)} type="button" style={{ marginRight: 'auto' }}
              title={record.stationCount > 0 ? 'Move its stations out first' : undefined}>
              <IconSystem.trash size={16} /> Delete
            </button>
          )}
          <button className="btn btn-secondary" onClick={onClose} type="button">Close</button>
          {canEdit && (
            <button className="btn btn-primary" onClick={() => onEdit(record)} type="button">
              <IconSystem.edit size={16} /> Edit
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default DineOpsKitchenViewModal;
